import { ChipMetadata, LinkButton, Modal } from '@pmi/dsm-react';
import { CheckmarkIcon } from '@pmi/dsm-react/dist/components/icons/avatar';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { Product, ProductLink } from '../../../Hub/TrainingHub/models';
import ChapterSelectionBlock, {
  Chapter,
} from '../../../Hub/TrainingHub/EnrollmentCard/ProductModal/EnhancedEnrollmentModal/ChapterSelectionBlock';

import CategoryLabel from '../CategoryLabel';
import { BundleOptions } from './models';
import { useRemoveProductMutation } from './apiSlice';
import { useAddToCartMutation } from '../../../Hub/TrainingHub/EnrollmentCard/apiSlice';
import { openCart } from '../../store/shared/cartSlice';
import { LocalChapter } from '../../../Hub/TrainingHub/EnrollmentCard/ProductModal/ProfileUpdateModal/models';

export interface ChangeChapterProps {
  product: Product;
  onCancel: () => void;
  defaultChapter: BundleOptions;
  toRemoveId: number;
  isLoading: boolean;
  localChapters: LocalChapter[];
}

const MAX_SUGGESTED_CHAPTERS = 3;
export default function ChangeChapter({
  product,
  onCancel,
  defaultChapter,
  toRemoveId,
  isLoading,
  localChapters,
}: ChangeChapterProps) {
  const { accountId } = useParams<{ accountId: string }>();
  const dispatch = useDispatch();

  // states
  const [chapterSelected, setChapterSelected] = useState<
    ProductLink | Chapter | null
  >(
    product.bundleOptions
      .find((x) => x.title.toLowerCase().includes('chapter'))
      ?.productLinks?.find((y) => y.sku === defaultChapter.selectionSku) ||
      null,
  );
  const [changeChapter, setChangeChapter] = useState(true);
  const [isChapterSearch, setIsChapterSearch] = useState(false);

  // mutations
  const [removeProduct] = useRemoveProductMutation();
  const [addToCart] = useAddToCartMutation();

  const chaptersInBundle = useMemo(() => {
    const chapterBundleOptions = product.bundleOptions
      .filter((bo) => bo.title.toLowerCase().includes('chapter'))
      .flatMap((x) => x.productLinks);

    return chapterBundleOptions.map((o) => ({ ...o } as Chapter));
  }, [product.bundleOptions]);

  const isChapterIncluded =
    product.isSingleMembershipEligible && chaptersInBundle?.length > 0;

  const getBundleOptions = () =>
    product.bundleOptions.map((b) => {
      if (b.title.toLowerCase().includes('chapter')) {
        const clone = { ...b };
        clone.productLinks = [chapterSelected];
        return clone;
      }
      return b;
    });

  const suggestChapters = () => {
    let list: Chapter[] = [];
    let preselectionList: Chapter[] = [];

    if (chapterSelected) {
      list = [
        {
          ...chapterSelected,
        } as Chapter,
      ];
    }

    // remove the pre-selected chapter on the list to ensure no duplication on the suggested chapters
    const cleaned = list.filter(
      (c) => c.chapterCode !== chapterSelected.chapterCode,
    );

    preselectionList = cleaned.concat(chaptersInBundle);

    if (localChapters?.length > 0) {
      preselectionList = preselectionList
        .filter((chapter) =>
          localChapters.some((lc) => lc.chapterSKU === chapter.sku),
        )
        .sort((a, b) => {
          const aIndex = localChapters.findIndex(
            (lc) => lc.chapterSKU === a.sku,
          );
          const bIndex = localChapters.findIndex(
            (lc) => lc.chapterSKU === b.sku,
          );
          return aIndex - bIndex;
        });
    }

    if (preselectionList.length > 0 && !chapterSelected) {
      setChapterSelected(preselectionList[0]);
    }
    return preselectionList.slice(0, MAX_SUGGESTED_CHAPTERS);
  };
  const [suggestedChapters, setSuggestedChapters] = useState(suggestChapters());
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    setSuggestedChapters([]);
  }, [chapterSelected]);

  useEffect(() => {
    setSuggestedChapters(suggestChapters());
  }, [chapterSelected, changeChapter]);

  const onConfirmClick = async () => {
    setIsPending(true);
    try {
      await removeProduct({ accountId, itemId: toRemoveId })
        .unwrap()
        .then(() => {
          const { sku, dealIds, price } = product;
          addToCart({
            accountId,
            items: [
              {
                qty: 1,
                sku,
                dealCode: dealIds[0],
                price,
                bundleOptions: getBundleOptions()?.map((option) => ({
                  optionId: option.id,
                  selectionId: parseInt(option.productLinks[0]?.id, 10),
                })),
              },
            ],
          })
            .unwrap()
            .then(() => {
              onCancel();
              dispatch(openCart());
            });
        })
        .finally(() => {
          setIsPending(false);
        });
    } catch (err) {
      console.error('Failed to remove product:', err);
    }
  };

  const onSelectChapter = (chapter: Chapter) => {
    setChapterSelected(chapter);
  };

  const chapterSelectionSection = () => (
    <div>
      <div className="d-flex align-content-center align-items-center mb-3">
        {chapterSelected && (
          <span className="d-block me-3">{chapterSelected.title}</span>
        )}
        <ChipMetadata
          text="Chapter Membership Included"
          icon={<CheckmarkIcon />}
          variant="violet"
        />
      </div>
      {chapterSelected && (
        <>
          <div className="d-block mb-3">
            <p>
              Whether you&apos;re a first-time member or renewing your
              membership, your membership includes access to a local PMI
              chapter. Enter your address to find a chapter near you and save a
              primary address in your profile.
            </p>
          </div>
          {!changeChapter &&
            !isChapterSearch &&
            chaptersInBundle.length > 1 && (
              <div className="d-block mb-3">
                <LinkButton
                  // id="change-chapter-btn" // add this
                  titleText="Change Chapter"
                  className="fw-normal text-decoration-underline"
                  onClick={() => {
                    setChangeChapter(true);
                  }}
                />
              </div>
            )}
        </>
      )}

      {!chapterSelected && (
        <p>
          As a part of your PMI Membership, you can select a chapter to be added
          to your membership at no additional cost. Please choose your preferred
          chapter below.
        </p>
      )}

      {(changeChapter || isChapterSearch) && (
        <div>
          <ChapterSelectionBlock
            isLoading={isLoading}
            suggestedChapters={suggestedChapters}
            selectedChapter={chapterSelected}
            chapters={chaptersInBundle}
            className="mt-3"
            onSearchChapter={(searchMode) => {
              setIsChapterSearch(searchMode);
            }}
            onSuggestedSelect={onSelectChapter}
            onSearchSelect={onSelectChapter}
          />
        </div>
      )}
    </div>
  );

  const confirmButtonText = 'Confirm and add to cart';

  return (
    <Modal
      buttons={[
        {
          titleText: 'Cancel',
          variant: 'outline-secondary',
          onClick: () => {
            onCancel();
          },
          disabled: isLoading,
        },
        {
          titleText: confirmButtonText,
          variant: 'primary',
          onClick: () => onConfirmClick(),
          disabled:
            isLoading ||
            isPending ||
            (isChapterIncluded && !chapterSelected) ||
            chapterSelected.sku === defaultChapter.selectionSku,
        },
      ]}
      onClose={() => onCancel()}
      variant="input"
      show
    >
      <div>
        {!product?.bundleOptions.some((x) =>
          x.title?.toLowerCase().includes('membership'),
        ) &&
          product?.bundleProduct && (
            <CategoryLabel type="Bundle" className="mb-3 me-2 float-start" />
          )}
        <CategoryLabel type={product.type} className="mb-3 d-inline-flex" />
      </div>
      <h3
        className="fs-4"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: product.name }}
      />
      {isChapterIncluded && chapterSelectionSection()}
    </Modal>
  );
}
